"use client";

import { cn } from "@/lib/utils";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

type ModalCtx = {
  open: (content: ReactNode, position?: ModalPosition) => void;
  close: () => void;
};

type ModalPosition = "center" | "bottom-right" | "bottom-left";

type ModalContent = {
  content: ReactNode;
  position?: ModalPosition;
};

const Ctx = createContext<ModalCtx | null>(null);

export function useModal() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useModal must be inside <ModalProvider>");
  }
  return ctx;
}

const getModalPosition = (position: ModalPosition = "center") => {
  switch (position) {
    case "bottom-right":
      return "items-end justify-end";
    case "bottom-left":
      return "items-end justify-start";
    case "center":
      return "items-center justify-center";
    default:
      return "items-center justify-center";
  }
};

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<ModalContent | null>(null);

  const open = (content: ReactNode, position?: ModalPosition) => {
    setModal({ content, position });
  };
  const close = () => {
    setModal(null);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [close]);

  return (
    <Ctx.Provider value={{ open, close }}>
      {children}

      {/* single global overlay */}
      {modal && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click is visual-only, not keyboard-interactive
        <div
          className={cn(
            "fixed inset-0 z-[9999] flex bg-black/40",
            getModalPosition(modal.position),
          )}
          onClick={close}
        >
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: This div uses onClick only to stop event propagation and is not interactive */}
          <div
            className={
              "animate-in animate-out transition-opacity duration-300 ease-out"
            }
            onClick={(e) => e.stopPropagation()}
          >
            {modal.content}
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
